import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, AlertTriangle, Clock, Mail, BellRing, Circle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { triggerOptimizer } from '@/services/stripe';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { isNativeApp } from '@/lib/platform';
import { trackCheckoutSuccess } from '@/utils/analytics';
import { DignityAvatar } from '@/components/scenario-builder/DignityAvatar';

const serif = "'Instrument Serif', Georgia, serif";

/**
 * Checkout success — two modes:
 *
 * NEW (draft-plan flow): URL has ?plan_id=… — the plan already exists (created
 * server-side BEFORE checkout). We poll its status while the Stripe webhook +
 * fulfillment worker build it: paid → building → ready. Staged progress from
 * fulfillment_log, honest ETA, email/push messaging. Safe to close the tab.
 *
 * LEGACY: no plan_id but a pending_care_plan_config in localStorage — the old
 * client-side creation path, kept so in-flight checkouts during deploy work.
 */
export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const planIdParam = searchParams.get('plan_id');
  const shareToken = searchParams.get('t');
  const navigate = useNavigate();
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const { t } = useTranslation('scenarios');
  const ahpDark = isAhp && isDark;
  const native = isNativeApp();

  const [status, setStatus] = useState<'creating' | 'optimizing' | 'done' | 'error' | 'polling'>(planIdParam ? 'polling' : 'creating');
  const [planStatus, setPlanStatus] = useState<string>('paid');
  const [fulfillmentLog, setFulfillmentLog] = useState<Array<{ step: string; label: string; done: boolean }>>([]);
  const [errorMsg, setErrorMsg] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const hasRun = useRef(false);
  const trackedRef = useRef(false);

  // ── NEW: poll the pre-created plan while the worker builds it ──
  useEffect(() => {
    if (!planIdParam) return;
    let stop = false;
    const startedAt = Date.now();

    const tick = async () => {
      if (stop) return;
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
      try {
        const { data } = await supabase
          .from('care_plans')
          .select('id, status, error_message, share_token, fulfillment_log')
          .eq('id', planIdParam)
          .maybeSingle();
        if (data) {
          setPlanStatus(data.status || 'paid');
          if (Array.isArray(data.fulfillment_log)) setFulfillmentLog(data.fulfillment_log as any);
          if (!trackedRef.current) { trackedRef.current = true; trackCheckoutSuccess(planIdParam, 0); }
          if (data.status === 'ready') {
            stop = true;
            navigate(`/plan/${planIdParam}?t=${shareToken || data.share_token || ''}`, { replace: true });
            return;
          }
          if (data.status === 'failed') {
            stop = true;
            setErrorMsg(data.error_message || (t('pricePlan.buildFailed', { defaultValue: 'Plan build hit a problem — our team has been notified. You will receive an email when it is fixed.' }) as string));
            setStatus('error');
            return;
          }
        }
      } catch { /* transient — keep polling */ }
      setTimeout(tick, 4000);
    };
    tick();
    return () => { stop = true; };
  }, [planIdParam, navigate, shareToken, t]);

  // ── LEGACY: client-side plan creation from localStorage ──
  useEffect(() => {
    if (planIdParam) return;
    if (hasRun.current) return;
    hasRun.current = true;

    async function processPayment() {
      try {
        const configStr = localStorage.getItem('pending_care_plan_config');
        if (!configStr) throw new Error('No pending care plan config found. Please try creating a plan again.');
        const config = JSON.parse(configStr);

        setStatus('creating');
        const { data: planData, error: planError } = await supabase
          .from('care_plans')
          .insert({
            anonymous_user_id: config.anonymous_user_id,
            plan_name: config.plan_name,
            latitude: config.latitude,
            longitude: config.longitude,
            location_label: config.location_label,
            location_state: config.location_state,
            search_radius_miles: config.search_radius_miles,
            insurance_status: config.insurance_status,
            consolidation_level: config.consolidation_level,
            user_email: config.user_email,
            family_member_name: config.family_member_name,
            stripe_session_id: sessionId,
            status: 'pending',
          })
          .select('id, share_token')
          .single();
        if (planError) throw planError;

        const scenarioRows = config.scenario_ids.map((sid: string, i: number) => ({
          care_plan_id: planData.id, scenario_id: sid, sort_order: i,
        }));
        const { error: scenError } = await supabase.from('care_plan_scenarios').insert(scenarioRows);
        if (scenError) throw scenError;

        setStatus('optimizing');
        try { await triggerOptimizer(planData.id); } catch (e) { console.warn('Optimizer trigger failed (non-fatal):', e); }

        localStorage.removeItem('pending_care_plan_config');
        setStatus('done');
        trackCheckoutSuccess(planData.id, config.scenario_ids?.length || 0);
        setTimeout(() => navigate(`/plan/${planData.id}?t=${planData.share_token}`, { replace: true }), 1500);
      } catch (err: any) {
        console.error('Checkout processing error:', err);
        setErrorMsg(err.message || 'Failed to create care plan');
        setStatus('error');
      }
    }
    processPayment();
  }, [planIdParam, sessionId, navigate]);

  const textColor = isAhp ? (ahpDark ? 'text-[#f0ebe3]' : 'text-gray-900') : 'text-foreground';
  const mutedColor = isAhp ? (ahpDark ? 'text-gray-400' : 'text-gray-600') : 'text-muted-foreground';
  const accentColor = isAhp ? 'text-[#002868]' : 'text-matrix-green';
  const gold = '#C5A55A';

  // Default staged checklist when the worker hasn't written a log yet.
  const stages = fulfillmentLog.length > 0 ? fulfillmentLog : [
    { step: 'paid', label: t('pricePlan.stagePaid', { defaultValue: 'Payment received' }) as string, done: true },
    { step: 'match', label: t('pricePlan.stageMatch', { defaultValue: 'Analyst verifying your matches (settings, quantities, bundles)' }) as string, done: ['building'].includes(planStatus) },
    { step: 'price', label: t('pricePlan.stagePrice', { defaultValue: 'Pricing your combination at every nearby hospital' }) as string, done: false },
    { step: 'optimize', label: t('pricePlan.stageOptimize', { defaultValue: 'Finding your cheapest arrangement' }) as string, done: false },
  ];

  const mins = Math.floor(elapsed / 60);

  // ── NEW-flow UI ──
  if (planIdParam && status !== 'error') {
    return (
      <div className={`min-h-screen flex items-center justify-center px-4 ${isAhp ? (ahpDark ? 'bg-[#0a1628]' : 'bg-[#F7F4EF]') : ''}`}>
        <div className="w-full max-w-md space-y-5 py-10">
          {/* Dignity presence */}
          <div className="flex items-center gap-3">
            <DignityAvatar size="chat" />
            <div>
              <div style={{ fontFamily: serif, fontSize: 15, color: gold }}>Dignity</div>
              <p className={`text-sm ${mutedColor}`} style={{ fontFamily: serif, fontStyle: 'italic' }}>
                {t('pricePlan.dignityWorking', { defaultValue: '“I\'m on it. Real analysis takes a few minutes — that\'s how you know it\'s real.”' })}
              </p>
            </div>
          </div>

          <div>
            <h1 className={`text-2xl font-bold ${textColor}`} style={{ fontFamily: serif, fontWeight: 400 }}>
              {t('pricePlan.buildingTitle', { defaultValue: 'Building your price plan' })}
            </h1>
            <p className={`text-sm mt-1 ${mutedColor}`}>
              {t('pricePlan.buildingSub', { defaultValue: 'Usually ready in about 15 minutes.' })}
              {mins >= 25 && ` ${t('pricePlan.takingLonger', { defaultValue: 'This one is taking longer than usual — it will finish, and we\'ll email you either way.' })}`}
            </p>
          </div>

          {/* Staged progress */}
          <div className="rounded-2xl p-4 space-y-3"
            style={{ background: ahpDark ? 'rgba(255,255,255,0.05)' : '#fff', border: `1px solid ${ahpDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,40,104,0.12)'}` }}>
            {stages.map((s, i) => {
              const isActive = !s.done && (i === 0 || stages[i - 1].done);
              return (
                <div key={s.step} className="flex items-start gap-2.5">
                  {s.done ? (
                    <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-green-500" />
                  ) : isActive ? (
                    <Loader2 className={`w-4 h-4 mt-0.5 shrink-0 animate-spin ${accentColor}`} />
                  ) : (
                    <Circle className={`w-4 h-4 mt-0.5 shrink-0 ${mutedColor}`} style={{ opacity: 0.4 }} />
                  )}
                  <span className={`text-sm ${s.done || isActive ? textColor : mutedColor}`}>{s.label}</span>
                </div>
              );
            })}
          </div>

          {/* You can close this */}
          <div className="rounded-xl p-3 flex items-start gap-2.5"
            style={{ background: 'rgba(197,165,90,0.08)', border: '1px solid rgba(197,165,90,0.25)' }}>
            {native ? <BellRing className="w-4 h-4 mt-0.5 shrink-0" style={{ color: gold }} /> : <Mail className="w-4 h-4 mt-0.5 shrink-0" style={{ color: gold }} />}
            <p className={`text-xs ${mutedColor}`} style={{ lineHeight: 1.5 }}>
              {native
                ? t('pricePlan.closeNoteNative', { defaultValue: 'You can safely close this screen — you\'ll get a push notification the moment your plan is ready, and it will be waiting under My Plans.' })
                : t('pricePlan.closeNoteWeb', { defaultValue: 'You can safely close this page — we\'ll email your private plan link the moment it\'s ready.' })}
            </p>
          </div>

          <div className={`flex items-center justify-between text-xs ${mutedColor}`}>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {mins}m {elapsed % 60}s</span>
            <Link to="/family" className="underline">{t('pricePlan.backToPlans', { defaultValue: 'My plans' })}</Link>
          </div>
        </div>
      </div>
    );
  }

  // ── Legacy + error UI ──
  return (
    <div className={`min-h-screen flex items-center justify-center ${isAhp ? (ahpDark ? 'bg-[#0a1628]' : 'bg-[#F7F4EF]') : ''}`}>
      <div className="text-center space-y-4 max-w-md px-4">
        {status === 'creating' && (
          <>
            <Loader2 className={`w-10 h-10 animate-spin mx-auto ${accentColor}`} />
            <h1 className={`text-xl font-bold ${textColor}`}>Payment Received!</h1>
            <p className={mutedColor}>Creating your care plan...</p>
          </>
        )}
        {status === 'optimizing' && (
          <>
            <Loader2 className={`w-10 h-10 animate-spin mx-auto ${accentColor}`} />
            <h1 className={`text-xl font-bold ${textColor}`}>Optimizing Your Plan</h1>
            <p className={mutedColor}>Finding the cheapest hospitals for each procedure...</p>
          </>
        )}
        {status === 'done' && (
          <>
            <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
            <h1 className={`text-xl font-bold ${textColor}`}>Plan Created!</h1>
            <p className={mutedColor}>Redirecting to your optimized care plan...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto" />
            <h1 className={`text-xl font-bold ${textColor}`}>Something Went Wrong</h1>
            <p className={mutedColor}>{errorMsg}</p>
            <button onClick={() => navigate('/family')} className={`text-sm underline ${accentColor}`}>
              Back to Family Planner
            </button>
          </>
        )}
      </div>
    </div>
  );
}
